import React, { useMemo, useRef } from 'react';
import { Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';
import numeral from 'numeral';
import { useReactToPrint } from 'react-to-print';
import './print.css';
import countries from '../../../../../assets/countries.json';

const KELOMPOK_POS_LABELS = {
    "01": "Barang RKSP / Barang yang diselesaikan di tempat",
    "02": "Barang import angkut lanjut",
    "03": "Barang import angkut terus",
    "04": "Barang export angkut lanjut"
};

const toText = (value, fallback = '-') => {
    if (value === null || value === undefined || value === '') return fallback;
    return `${value}`;
};

const joinValues = (...values) => {
    const cleaned = values
        .map(value => {
            if (value === null || value === undefined || value === '' || value === '-') {
                return null;
            }
            return `${value}`;
        })
        .filter(Boolean);
    return cleaned.length ? cleaned.join(' / ') : '-';
};

const formatDate = (value) => {
    if (!value) return '-';
    const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : `${value}`;
};

const formatNumber = (value, format = '0,0.0000') => {
    if (value === null || value === undefined || value === '') return '-';
    const numeric = Number(`${value}`.replace(/,/g, ''));
    if (Number.isNaN(numeric)) return `${value}`;
    return numeral(numeric).format(format);
};

const getNumericOrderValue = (value) => {
    if (value === undefined || value === null) return 0;
    const numeric = parseInt(`${value}`.replace(/[^0-9]/g, ''), 10);
    return Number.isNaN(numeric) ? 0 : numeric;
};

const sortDetilRecords = (records = []) => {
    if (!Array.isArray(records)) return [];
    return [...records].sort((a = {}, b = {}) => {
        const kdDifference = getNumericOrderValue(a?.kd_kelompok_pos) - getNumericOrderValue(b?.kd_kelompok_pos);
        if (kdDifference !== 0) {
            return kdDifference;
        }
        return getNumericOrderValue(a?.no_pos) - getNumericOrderValue(b?.no_pos);
    });
};

const groupBy = (items = [], key) => items.reduce((acc, item) => {
    const groupKey = item?.[key];
    if (!groupKey) return acc;
    acc[groupKey] = acc[groupKey] || [];
    acc[groupKey].push(item);
    return acc;
}, {});

const formatKelompok = (value) => {
    if (!value) return '-';
    const code = `${value}`.trim();
    const label = KELOMPOK_POS_LABELS[code];
    return label ? `${label} (${code})` : code;
};

const formatPos = (item = {}) => {
    const values = [item.no_pos, item.no_sub_pos, item.no_sub_sub_pos].filter(Boolean);
    return values.length ? values.join(' ') : '-';
};

const RkspPrintContent = React.forwardRef(({ data, configCache }, ref) => {
    const header = data?.header?.[0] || {};
    const details = Array.isArray(data?.detil) ? data.detil : [];
    const sortedDetails = useMemo(() => sortDetilRecords(details), [details]);
    const barangByDetil = useMemo(() => groupBy(data?.barang || [], 'id_detil'), [data?.barang]);
    const kontainerByDetil = useMemo(() => groupBy(data?.kontainer || [], 'id_detil'), [data?.kontainer]);
    const kelompokCode = details?.[0]?.kd_kelompok_pos || header?.kd_jns_manifest;
    const companyName = (configCache?.name || header?.handling_agent || header?.pemberitahu || '').toUpperCase();
    const companyNpwp = configCache?.npwp || header?.npwp || '-';
    const companyAddress = configCache?.address || '-';
    const companyKantor = configCache?.kode_kantor || header?.kppbc || '';

    const countryMap = useMemo(() => {
        if (!Array.isArray(countries)) return {};
        return countries.reduce((acc, item) => {
            if (item?.code) acc[item.code.toUpperCase()] = item.name;
            return acc;
        }, {});
    }, []);

    const renderCountryName = (code) => {
        if (!code) return '-';
        const found = countryMap[`${code}`.toUpperCase()];
        return found || `${code}`;
    };

    return (
        <div ref={ref} className="rksp-print">
            <div className="rksp-print__top">
                <div className="rksp-print__top-code">BC 1.0</div>
                <div className="rksp-print__top-left">
                    <div className="rksp-print__title">RKSP</div>
                    <div className="rksp-print__row">
                        <span className="rksp-print__label">Kantor Pabean</span>
                        <span className="rksp-print__value">: {toText(header.kppbc)}/KPU SOEKARNO-HATTA</span>
                    </div>
                    <div className="rksp-print__row">
                        <span className="rksp-print__label">Kelompok</span>
                        <span className="rksp-print__value">: {formatKelompok(kelompokCode)}</span>
                    </div>
                </div>
                <div className="rksp-print__top-middle">
                    <div className="rksp-print__mid-row">
                        <span className="rksp-print__mid-label">No.Pengajuan</span>
                        <span className="rksp-print__mid-value">: {toText(header.nomor_aju)}</span>
                        <span>Tgl.</span>
                        <span>: {formatDate(header.tgl_bc_10 || header.tgl_bc_11)}</span>
                    </div>
                    <div className="rksp-print__mid-row">
                        <span className="rksp-print__mid-label">No.BC 1.0</span>
                        <span className="rksp-print__mid-value">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; : {toText(header.no_bc_10)}</span>
                        <span className="rksp-print__mid-label">Tgl.</span>
                        <span className="rksp-print__mid-value">: {formatDate(header.tgl_bc_10)}</span>
                    </div>
                    <div className="rksp-print__mid-row">
                        <span className="rksp-print__mid-label">No.BC 1.1</span>
                        <span className="rksp-print__mid-value">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; : {toText(header.no_bc_11)}</span>
                        <span className="rksp-print__mid-label">Tgl.</span>
                        <span className="rksp-print__mid-value">: {formatDate(header.tgl_bc_11)}</span>
                    </div>
                </div>
                <div className="rksp-print__top-right">
                    <div className="rksp-print__company-name">{companyName || '-'} / {companyNpwp}</div>
                    <div>{companyAddress}</div>
                    {companyKantor && <div>{companyKantor}</div>}
                </div>
            </div>
            <div className="rksp-print__divider" />
            <div className="rksp-print__info">
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Nama Sarana Pengangkut</span>
                    <span className="rksp-print__info-value">: {toText(header.nama_sarana_angkut)}</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Pelabuhan Asal / Transit &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    <span className="rksp-print__info-value">
                        : {renderCountryName(header.pelabuhan_asal?.slice(0, 2))} / {renderCountryName(header.pelabuhan_transit?.slice(0, 2))}
                    </span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Jml. MBL/MAWB</span>
                    <span className="rksp-print__info-value">: {formatNumber(header.total_master_bl_awb, '0,0')}</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Bruto</span>
                    <span className="rksp-print__info-value">: {formatNumber(header.total_bruto)} Kgm</span>
                </div>

                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">No. Voy/Flight</span>
                    <span className="rksp-print__info-value">: {toText(header.no_voyage_arrival || header.call_sign)}</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Pelabuhan Tujuan / Selanjutnya</span>
                    <span className="rksp-print__info-value">
                        : {renderCountryName(header.pelabuhan_bongkar?.slice(0, 2))} / {renderCountryName(header.pelabuhan_selanjutnya?.slice(0, 2))}
                    </span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Jml. HBL/HAWB</span>
                    <span className="rksp-print__info-value">: {formatNumber(header.total_pos, '0,0')}</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Volume</span>
                    <span className="rksp-print__info-value">: {formatNumber(header.total_volume)} M3</span>
                </div>

                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Bendera</span>
                    <span className="rksp-print__info-value">: {toText(header.negara)} / {renderCountryName(header.negara).toUpperCase()}</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Perkiraan Waktu Tiba/Brgkt &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    {/* <span className="rksp-print__info-value">: {joinValues(header.jam_tiba, header.jam_keberangkatan)}</span> */}
                    <span className="rksp-print__info-value">: {toText(header?.tgl_tiba)} {toText(header?.jam_tiba)}/-</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Jml. Peti Kemas</span>
                    <span className="rksp-print__info-value">: {formatNumber(header.total_kontainer, '0,0')}</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">GRT/LOA</span>
                    <span className="rksp-print__info-value">: -</span>
                </div>

                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">No.IMO/MMSI/Reg./Pol.</span>
                    <span className="rksp-print__info-value">: -/-/{toText(header.no_voyage_arrival || header.call_sign)}/-</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Waktu Tiba/Bongkar/Muat &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    {/* <span className="rksp-print__info-value">: {joinValues(header.jam_tiba, header.jam_bongkar, header.jam_muat)}</span> */}
                    <span className="rksp-print__info-value">: -/-/-</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Jml. Kemasan</span>
                    <span className="rksp-print__info-value">: {formatNumber(header.total_kemasan, '0,0')}</span>
                </div>
                <div className="rksp-print__info-item">
                    <span className="rksp-print__info-label">Draft D/B</span>
                    <span className="rksp-print__info-value">: -</span>
                </div>
            </div>
            <table className="rksp-print__table">
                <thead>
                    <tr>
                        <th className="rksp-print__col-pos">No. Pos</th>
                        <th className="rksp-print__col-doc">Nomor & Tgl B/L/AWB/Lain, Nomor & Tgl PEB</th>
                        <th className="rksp-print__col-party">Nama, NPWP, & Alamat Shipper/Consignee</th>
                        <th className="rksp-print__col-pack">Merek, Jumlah & Jenis Kemasan, Nomor Peti Kemas</th>
                        <th className="rksp-print__col-goods">Uraian Barang, Bruto/Volume</th>
                        <th className="rksp-print__col-note">Keterangan, Pelabuhan Asal/Muat/Transit/Bongkar</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedDetails.length > 0 ? sortedDetails.map((item, index) => {
                        console.log({ item })
                        const barangItems = barangByDetil[item?.id_detil] || [];
                        const kontainerItems = kontainerByDetil[item?.id_detil] || [];
                        const kontainerNumbers = kontainerItems
                            .map(kontainer => kontainer?.nomor_kontainer)
                            .filter(Boolean);

                        return (
                            <tr key={`${item?.id_detil || index}`}>
                                <td>
                                    <div className="rksp-print__line">{toText(item?.kd_kelompok_pos)}{formatPos(item)} {item?.isconsole == true ? "(c)" : item?.ispartial == true ? "(p)" : ""}</div>
                                </td>
                                <td>
                                    <span>Master :</span><br />
                                    <span> &nbsp;&nbsp; - {toText(item?.no_master_blawb)}</span><br />
                                    <span> &nbsp;&nbsp; - {formatDate(item?.tgl_master_blawb)}</span><br />
                                    <span>House :</span><br />
                                    <span> &nbsp;&nbsp; - {toText(item?.no_host_blawb)}</span><br />
                                    <span> &nbsp;&nbsp; - {formatDate(item?.tgl_host_blawb)}</span><br />
                                    <span>Dokumen PIB :</span><br />
                                    <span> &nbsp;&nbsp; - -</span><br />
                                    <span> &nbsp;&nbsp; - -</span><br />
                                    <span> &nbsp;&nbsp; - -</span><br />
                                    <span>Mother Vessel :</span><br />
                                    <span> &nbsp;&nbsp; - -</span><br />
                                </td>
                                <td>
                                    <span className="rksp-print__label-inline">Shipper:</span>
                                    <div className="rksp-print__line">
                                        &nbsp;&nbsp; - {toText(item?.nama_shipper)} {item?.npwp_shipper ? `(${item?.npwp_shipper})` : ''}
                                    </div>
                                    <div className="rksp-print__line">&nbsp;&nbsp; - {toText(item?.almt_shipper)}</div>

                                    <span className="rksp-print__label-inline">Consignee:</span>
                                    <div className="rksp-print__line">
                                        &nbsp;&nbsp; - {toText(item?.nama_consignee)} {item?.npwp_consignee ? `(${item?.npwp_consignee})` : ''}
                                    </div>
                                    <div className="rksp-print__line">&nbsp;&nbsp; - {toText(item?.almt_consignee)}</div>

                                    <span className="rksp-print__label-inline">Notify:</span>
                                    <div className="rksp-print__line">
                                        &nbsp;&nbsp; - {toText(item?.nama_notify)} {item?.npwp_notify ? `(${item?.npwp_notify})` : ''}
                                    </div>
                                    <div className="rksp-print__line">&nbsp;&nbsp; - {toText(item?.almt_notify)}</div>
                                </td>
                                <td>
                                    <div className="rksp-print__line">{toText(item?.merk_kemasan)}</div>
                                    <div className="rksp-print__line">
                                        {formatNumber(item?.jumlah_kemasan, '0,0')}/{formatNumber(item?.total_kemasan, '0,0')} {toText(item?.jenis_kemasan, '')}
                                    </div>
                                    {kontainerNumbers.length > 0 ? (
                                        <div className="rksp-print__line">{kontainerNumbers.join(', ')}</div>
                                    ) : (
                                        <div className="rksp-print__line">{toText(item?.jumlah_kontainer)}</div>
                                    )}
                                </td>
                                <td>
                                    {barangItems.length > 0 ? barangItems.map((barang, idx) => (
                                        <div
                                            key={`${item?.id_detil}-${idx}`}
                                            style={{ display: "flex", gap: "2px" }}
                                        >
                                            <div>{toText(barang?.hs_code)}</div>
                                            <div>{toText(barang?.uraian_barang)}</div>
                                        </div>
                                    )) : (
                                        <div className="rksp-print__line">-</div>
                                    )}
                                    <div className="rksp-print__line">Bruto Total: {formatNumber(item?.bruto)}</div>
                                    <div className="rksp-print__line">Volume Total: {formatNumber(item?.volume)}</div>
                                </td>
                                <td>
                                    <div className="rksp-print__line">{toText(item?.pelabuhan_asal)}/{renderCountryName(item.pelabuhan_asal?.slice(0, 2))}</div>
                                    <div className="rksp-print__line">{toText(item?.pelabuhan_transit)}/{renderCountryName(item.pelabuhan_transit?.slice(0, 2))}</div>
                                    <div className="rksp-print__line">{toText(item?.pelabuhan_bongkar)}/{renderCountryName(item.pelabuhan_bongkar?.slice(0, 2))}</div>
                                    <div className="rksp-print__line">{toText(item?.pelabuhan_akhir)}/{renderCountryName(item.pelabuhan_akhir?.slice(0, 2))}</div>
                                </td>
                            </tr>
                        );
                    }) : (
                        <tr>
                            <td colSpan={6} className="rksp-print__empty">Tidak ada data.</td>
                        </tr>
                    )}
                </tbody>
            </table>
            <div className="rksp-print__footer">
                <div className="rksp-print__notes">
                    Keterangan: (B) Pembatalan; (P) Pecah Pos; (E) Partial; (C) Konsolidasi; (*) Kontainer Tertinggal
                </div>
                <div className="rksp-print__signature">
                    <div>{companyName || '-'}</div>
                    <div className="rksp-print__signature-name">{header?.user || ''}</div>
                </div>
            </div>
        </div>
    );
});

export const RkspPrintButton = ({ data, configCache }) => {
    const printRef = useRef(null);
    const documentTitle = data?.header?.[0]?.nomor_aju
        ? `rksp-${data?.header?.[0]?.nomor_aju}`
        : 'rksp-print';
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle
    });

    return (
        <>
            <Button
                shape='circle'
                icon={<PrinterOutlined />}
                type="link"
                title='Print'
                onClick={handlePrint}
                style={{ color: '#222' }}
            />
            <div style={{ display: 'none' }}>
                <RkspPrintContent ref={printRef} data={data} configCache={configCache} />
            </div>
        </>
    );
};
